import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Megaphone,
  ListTodo,
  Send,
  BarChart3,
  Settings,
  Check,
  RefreshCw,
  Plus,
  Minus,
  CalendarDays,
  Sparkles,
  Save,
  X,
  LogOut,
  HelpCircle,
} from 'lucide-react';
import { adminApi } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import MarketingAnalytics from '../components/MarketingAnalytics';
import RepurposeManager from '../components/RepurposeManager';

const TABS = [
  { id: 'settings', label: '1. Setup', icon: Settings },
  { id: 'queue', label: '2. Create', icon: ListTodo },
  { id: 'campaigns', label: '3. Launch', icon: Megaphone },
  { id: 'repurpose', label: '4. Repurpose', icon: Sparkles },
  { id: 'published', label: '5. Results', icon: Send },
  { id: 'analytics', label: '6. Analyze', icon: BarChart3 },
];

const UI_TO_ENGINE_CONTENT_TYPE = {
  reel: 'short_script',
  short_video: 'short_script',
  script: 'short_script',
  story: 'short_script',
  video: 'short_script',
  carousel: 'linkedin_carousel',
  article: 'blog_post',
  post: 'comparison_script',
  post_caption: 'comparison_script',
  newsletter: 'newsletter_section',
};

const EMPTY_CAMPAIGN = {
  id: '',
  title: '',
  subject: '',
  preview_text: '',
  audience_tag: 'all',
  status: 'draft',
  content: '',
  cta_label: 'Read more',
  cta_url: '',
  scheduled_for: '',
};

// Platform-specific content types and media requirements
const PLATFORM_CONTENT_MATRIX = {
  Instagram: {
    reel: { label: 'Reel (Video)', media: ['video', 'audio'], description: 'Short-form video content (15-90s)' },
    carousel: { label: 'Carousel Post', media: ['images'], description: 'Multiple images with swipe navigation' },
    story: { label: 'Story', media: ['image', 'video'], description: 'Ephemeral vertical content (24h)' },
    post_caption: { label: 'Post + Caption', media: ['image', 'text'], description: 'Image with engaging caption' },
  },
  LinkedIn: {
    article: { label: 'Article', media: ['document', 'text', 'image'], description: 'Long-form professional content' },
    post: { label: 'Post', media: ['text', 'image', 'document'], description: 'Text post with optional media' },
    newsletter: { label: 'Newsletter', media: ['document', 'text'], description: 'Curated professional digest' },
    carousel: { label: 'Carousel', media: ['images', 'document'], description: 'Document-style carousel' },
  },
  TikTok: {
    reel: { label: 'Reel (Video)', media: ['video', 'audio'], description: 'Short-form video (15-60s)' },
    short_video: { label: 'Short Video', media: ['video'], description: 'Native TikTok video format' },
    script: { label: 'Video Script', media: ['text'], description: 'Script for content creators' },
  },
  YouTube: {
    video: { label: 'Video', media: ['video', 'script', 'thumbnail'], description: 'Long-form or short video' },
    short: { label: 'YouTube Short', media: ['video'], description: 'Vertical short-form video' },
    article: { label: 'Article/Blog', media: ['document', 'text'], description: 'Blog post or companion article' },
    script: { label: 'Video Script', media: ['text'], description: 'Detailed video script' },
  },
};

const PLATFORMS = Object.keys(PLATFORM_CONTENT_MATRIX);

const MEDIA_TYPES = {
  video: { label: 'Video', color: 'text-red-400' },
  audio: { label: 'Audio', color: 'text-purple-400' },
  image: { label: 'Image', color: 'text-yellow-400' },
  images: { label: 'Images', color: 'text-yellow-400' },
  text: { label: 'Text', color: 'text-blue-400' },
  document: { label: 'Document', color: 'text-orange-400' },
  script: { label: 'Script', color: 'text-cyan-400' },
  thumbnail: { label: 'Thumbnail', color: 'text-green-400' },
};

const STATUS_CLASS = {
  queued: 'text-cyber-yellow border-cyber-yellow/40 bg-cyber-yellow/10',
  published: 'text-laser-green border-laser-green/40 bg-laser-green/10',
  skipped: 'text-text-muted border-white/20 bg-white/5',
};

const TYPE_CLASS = {
  short: 'bg-cyan-400/10 text-cyan-300 border-cyan-300/40',
  blog: 'bg-blue-400/10 text-blue-300 border-blue-300/40',
  newsletter: 'bg-fuchsia-400/10 text-fuchsia-300 border-fuchsia-300/40',
  carousel: 'bg-orange-400/10 text-orange-300 border-orange-300/40',
};

const EMPTY_SETTINGS = {
  auto_schedule_enabled: true,
  newsletter_auto_send: false,
  sitemap_auto_ping: true,
  require_approval_before_publish: true,
  smart_posting_windows_enabled: true,
  auto_repurpose_enabled: false,
  auto_utm_enabled: true,
  ab_variant_count: 2,
  recycle_after_days: 21,
  campaign_tag: 'spring-launch',
  utm_source: 'wowrolla',
  utm_medium: 'social',
  utm_campaign: 'ai-tool-promo',
};

const EMPTY_OPS_CONFIG = {
  convertkit_api_key: '',
  convertkit_api_secret: '',
  buffer_access_token: '',
  make_webhook_url: '',
  resend_api_key: '',
  resend_from_email: 'marketing@wowrolla.ca',
  google_client_id: '',
  google_client_secret: '',
  google_redirect_uri: '',
  default_timezone: 'UTC',
  consulting_leads_email: 'strategy@wowrolla.ca',
  default_partner_manager_email: 'partners@wowrolla.ca',
  flonnect_webhook_url: '',
};

const nowIso = () => new Date().toISOString();

const normalizeQueueItem = (item = {}) => ({
  id: item.id || item._id || crypto.randomUUID(),
  campaign_id: item.campaign_id || item.campaignId || '',
  tool_id: item.tool_id || item.toolId || '',
  tool_name: item.tool_name || item.toolName || item.title || 'Unknown Tool',
  title: item.title || `${item.tool_name || 'Tool'} ${item.content_type || 'content'}`,
  content_type: (item.content_type || item.contentType || 'short').toLowerCase(),
  platforms: Array.isArray(item.platforms) ? item.platforms : [],
  scheduled_at: item.scheduled_time || item.scheduled_at || item.scheduledAt || item.created_at || item.createdAt || nowIso(),
  status: (item.status || 'queued').toLowerCase(),
  created_at: item.created_at || item.createdAt || nowIso(),
  views: Number(item.views || item.stats?.views || 0),
  likes: Number(item.likes || item.stats?.likes || 0),
  clicks: Number(item.clicks || item.stats?.clicks || 0),
  platform: item.platform || '',
  repurpose_job_id: item.repurpose_job_id || '',
  content: item.content || {},
  carousel: item.carousel || null,
  youtube_metadata_id: item.youtube_metadata_id || null,
  raw: item,
});

const extractQueueItems = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

const isWithinDays = (isoDate, days) => {
  const at = new Date(isoDate).getTime();
  if (Number.isNaN(at)) return false;
  const cutoff = Date.now() - days * 24 * 60 * 60 * 1000;
  return at >= cutoff;
};

const mockEngagement = (id) => {
  const seed = [...String(id)].reduce((acc, c) => acc + c.charCodeAt(0), 0);
  return {
    views: 500 + (seed % 5000),
    likes: 25 + (seed % 950),
    clicks: 10 + (seed % 500),
  };
};

// Get required media for a content type on a platform
const getRequiredMediaForContent = (platform, contentType) => {
  const matrix = PLATFORM_CONTENT_MATRIX[platform];
  return matrix?.[contentType]?.media || [];
};

const RECOMMENDED_WINDOWS = {
  LinkedIn: 'Tue-Thu, 8:30-10:30 AM',
  YouTube: 'Wed-Sat, 12:00-3:00 PM',
  TikTok: 'Daily, 6:00-9:00 PM',
  Instagram: 'Mon-Fri, 11:00 AM-1:00 PM',
};

const MarketingPage = () => {
  const { isAuthenticated, loading: authLoading, logout, user } = useAuth();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('settings');
  const [campaign, setCampaign] = useState(EMPTY_CAMPAIGN);
  const [recentCampaigns, setRecentCampaigns] = useState([]);
  const [savingCampaign, setSavingCampaign] = useState(false);
  const [campaignActionBusy, setCampaignActionBusy] = useState('');
  const [queueItems, setQueueItems] = useState([]);
  const [tools, setTools] = useState([]);
  const [settings, setSettings] = useState(EMPTY_SETTINGS);
  const [opsConfig, setOpsConfig] = useState(EMPTY_OPS_CONFIG);
  const [opsDiagnostics, setOpsDiagnostics] = useState([]);
  const [expandedSettingsSections, setExpandedSettingsSections] = useState({
    automation: true,
    posting: true,
    tracking: false,
    windows: false,
    integrations: true,
  });
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loadingPage, setLoadingPage] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);
  const [savingOpsConfig, setSavingOpsConfig] = useState(false);
  const [generateForm, setGenerateForm] = useState({
    tool_id: '',
    content_type: 'reel',
    platforms: ['Instagram'],
    ai_guidance: '',
    media_assets: [],
  });

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate('/admin');
      return;
    }

    const load = async () => {
      setLoadingPage(true);
      try {
        const [queueRes, toolsRes, settingsRes, opsConfigRes, dashboardRes, diagnosticsRes] = await Promise.all([
          adminApi.getQueue().catch(() => ({ data: [] })),
          adminApi.getTools().catch(() => ({ data: [] })),
          adminApi.getSettings().catch(() => ({ data: {} })),
          adminApi.getOpsConfig().catch(() => ({ data: {} })),
          adminApi.getNewsletterDashboard().catch(() => ({ data: { campaigns: [] } })),
          adminApi.validateOpsConfig().catch(() => ({ data: { checks: [] } })),
        ]);

        const normalizedQueue = extractQueueItems(queueRes?.data).map(normalizeQueueItem);
        setQueueItems(normalizedQueue);

        const toolsList = toolsRes?.data || [];
        setTools(toolsList);

        setGenerateForm((prev) => ({
          ...prev,
          tool_id: prev.tool_id || toolsList?.[0]?.id || '',
        }));

        setSettings({
          ...EMPTY_SETTINGS,
          ...(settingsRes?.data || {}),
        });

        setOpsConfig({
          ...EMPTY_OPS_CONFIG,
          ...(opsConfigRes?.data || {}),
        });

        setRecentCampaigns(dashboardRes?.data?.campaigns || []);
        setOpsDiagnostics(diagnosticsRes?.data?.checks || []);
      } catch (error) {
        console.error('Failed to load marketing dashboard data:', error);
      } finally {
        setLoadingPage(false);
      }
    };

    load();
  }, [authLoading, isAuthenticated, navigate]);

  const publishedThisWeek = useMemo(() => (
    queueItems
      .filter((item) => {
        const status = String(item.status || '').toLowerCase();
        const contentType = String(item.content_type || '').toLowerCase();
        const isPublishedStatus = status === 'published';
        const isNewsletterDelivered = contentType === 'newsletter_campaign' && ['scheduled', 'sent'].includes(status);
        const activityDate = item.published_at || item.updated_at || item.created_at;
        return (isPublishedStatus || isNewsletterDelivered) && isWithinDays(activityDate, 7);
      })
      .map((item) => {
        const fallback = mockEngagement(item.id);
        return {
          ...item,
          views: item.views || fallback.views,
          likes: item.likes || fallback.likes,
          clicks: item.clicks || fallback.clicks,
        };
      })
      .sort((a, b) => new Date(b.published_at || b.updated_at || b.created_at).getTime() - new Date(a.published_at || a.updated_at || a.created_at).getTime())
  ), [queueItems]);

  const queueFeatureStats = useMemo(() => {
    const queued = queueItems.filter((i) => i.status === 'queued').length;
    const pendingApproval = settings.require_approval_before_publish ? queued : 0;
    const repurposeCandidates = publishedThisWeek.filter((i) => i.clicks < 50 || i.likes < 60).length;
    const utmCoverage = Math.min(100, Math.round((queueItems.filter((i) => (i.platforms || []).length).length / Math.max(queueItems.length, 1)) * 100));
    return { queued, pendingApproval, repurposeCandidates, utmCoverage };
  }, [publishedThisWeek, queueItems, settings.require_approval_before_publish]);

  const integrationState = useMemo(() => {
    const findStatus = (name) => opsDiagnostics.find((item) => item.name === name)?.status;
    return {
      makeReady: !!String(opsConfig.make_webhook_url || '').trim() && findStatus('Make Webhook') !== 'missing',
      bufferReady: !!String(opsConfig.buffer_access_token || '').trim() && findStatus('Buffer') !== 'missing',
      resendReady: !!String(opsConfig.resend_api_key || '').trim() && findStatus('Resend') !== 'missing',
      googleReady: !!String(opsConfig.google_client_id || '').trim() && !!String(opsConfig.google_client_secret || '').trim() && !!String(opsConfig.google_redirect_uri || '').trim(),
    };
  }, [opsConfig, opsDiagnostics]);

  const campaignRequirements = useMemo(() => {
    const requirements = [];
    if (!integrationState.makeReady) requirements.push('Add a valid Make webhook in Setup so campaigns have an automation destination.');
    if (!String(campaign.title || '').trim()) requirements.push('Enter a campaign title.');
    if (!String(campaign.subject || '').trim()) requirements.push('Enter a subject line.');
    if (!String(campaign.content || '').trim()) requirements.push('Write or paste the campaign body content.');
    if (!String(campaign.scheduled_for || '').trim()) requirements.push('Choose an ISO schedule time before queuing the campaign for cron.');
    return requirements;
  }, [campaign, integrationState.makeReady]);

  const workflowStages = useMemo(() => {
    const hasGeneratedQueue = queueItems.length > 0;
    const hasScheduledCampaign = recentCampaigns.some((item) => ['scheduled', 'processing', 'sent'].includes(String(item.status || '').toLowerCase()));
    const hasResults = publishedThisWeek.length > 0;

    return [
      {
        id: 'settings',
        title: 'Connect automations',
        complete: integrationState.makeReady && (integrationState.bufferReady || integrationState.resendReady),
        hint: integrationState.makeReady ? 'Automation destination is configured.' : 'Start by adding Make, Buffer, and email credentials.',
      },
      {
        id: 'queue',
        title: 'Generate campaign assets',
        complete: hasGeneratedQueue,
        hint: hasGeneratedQueue ? 'Content is ready for review.' : 'Generate at least one content asset for this cycle.',
      },
      {
        id: 'campaigns',
        title: 'Queue the campaign',
        complete: hasScheduledCampaign,
        hint: hasScheduledCampaign ? 'Campaign is ready for cron or dispatch.' : 'Save a campaign, then queue it for cron or publish it now.',
      },
      {
        id: 'published',
        title: 'Confirm results',
        complete: hasResults,
        hint: hasResults ? 'This cycle has published output.' : 'Watch this tab after publish or cron execution.',
      },
    ];
  }, [integrationState, queueItems.length, recentCampaigns, publishedThisWeek.length]);

  const nextStage = workflowStages.find((stage) => !stage.complete)?.id || 'analytics';

  const publishQueueItem = async (id) => {
    setBusy(true);
    try {
      await adminApi.publishItem(id);
      await refreshQueue();
    } catch (error) {
      console.error('Failed to publish queue item:', error);
      alert('Failed to publish this queued item. Check Setup credentials and queue configuration.');
    } finally {
      setBusy(false);
    }
  };

  const publishTodayBatch = async () => {
    setBusy(true);
    try {
      const today = new Date().toISOString().slice(0, 10);
      await adminApi.publishBatch(today);
      await refreshQueue();
      alert('Scheduled items for today were sent to the publisher.');
    } catch (error) {
      console.error('Failed to run batch publish:', error);
      alert('Batch publishing failed. Check Setup credentials and scheduled items.');
    } finally {
      setBusy(false);
    }
  };

  const saveCurrentCampaign = async () => {
    const response = await adminApi.saveNewsletterCampaign(campaign);
    const savedCampaign = response?.data || {};
    setCampaign((prev) => ({ ...prev, id: savedCampaign.id || prev.id, status: savedCampaign.status || prev.status }));
    const dash = await adminApi.getNewsletterDashboard();
    setRecentCampaigns(dash.data?.campaigns || []);
    return savedCampaign;
  };

  const automateCampaign = async (action) => {
    if (action === 'schedule' && campaignRequirements.length > 0) {
      alert(campaignRequirements[0]);
      return;
    }

    if (action === 'publish_now') {
      const publishNowRequirements = [];
      if (!integrationState.makeReady) publishNowRequirements.push('Add a valid Make webhook in Setup before using Publish via Make now.');
      if (!String(campaign.title || '').trim()) publishNowRequirements.push('Enter a campaign title.');
      if (!String(campaign.subject || '').trim()) publishNowRequirements.push('Enter a subject line.');
      if (!String(campaign.content || '').trim()) publishNowRequirements.push('Write or paste the campaign body content.');
      if (publishNowRequirements.length > 0) {
        alert(publishNowRequirements[0]);
        return;
      }
    }

    setCampaignActionBusy(action);
    try {
      const savedCampaign = await saveCurrentCampaign();
      await adminApi.automateNewsletterCampaign(savedCampaign.id, {
        action,
        scheduled_for: campaign.scheduled_for || null,
      });
      await refreshQueue();
      const dash = await adminApi.getNewsletterDashboard();
      setRecentCampaigns(dash.data?.campaigns || []);
      setCampaign((prev) => ({ ...prev, id: savedCampaign.id || prev.id, status: action === 'schedule' ? 'scheduled' : 'sent' }));
      setActiveTab(action === 'schedule' ? 'published' : 'published');
      alert(action === 'schedule' ? 'Campaign was queued for cron publishing.' : 'Campaign was dispatched through Make automation.');
    } catch (error) {
      console.error('Campaign automation failed:', error);
      alert('Campaign automation failed. Finish the required fields in this tab and confirm Setup credentials.');
    } finally {
      setCampaignActionBusy('');
    }
  };

  const refreshQueue = async () => {
    setBusy(true);
    try {
      const response = await adminApi.getQueue();
      setQueueItems(extractQueueItems(response?.data).map(normalizeQueueItem));
    } catch (error) {
      console.error('Failed to refresh queue:', error);
    } finally {
      setBusy(false);
    }
  };

  const markPublished = async (id) => {
    try {
      await adminApi.markQueuePublished(id, { status: 'published' });
      setQueueItems((prev) => prev.map((item) => (item.id === id ? { ...item, status: 'published' } : item)));
    } catch (error) {
      console.error('Failed to mark queue item as published:', error);
      alert('Failed to mark content as published.');
    }
  };

  const togglePlatform = (platform) => {
    setGenerateForm((prev) => {
      const current = prev.platforms || [];
      const exists = current.includes(platform);
      const newPlatforms = exists ? current.filter((p) => p !== platform) : [...current, platform];

      // Reset content type if not available for any selected platform
      let newContentType = prev.content_type;
      if (newPlatforms.length > 0) {
        const validTypes = new Set();
        newPlatforms.forEach((p) => {
          const types = PLATFORM_CONTENT_MATRIX[p];
          if (types) Object.keys(types).forEach((t) => validTypes.add(t));
        });
        if (!validTypes.has(newContentType)) {
          newContentType = [...validTypes][0] || 'reel';
        }
      }

      return {
        ...prev,
        platforms: newPlatforms,
        content_type: newContentType,
      };
    });
  };

  const submitGenerate = async (e) => {
    e.preventDefault();
    if (!generateForm.tool_id || !(generateForm.platforms || []).length) {
      alert('Select a tool and at least one platform.');
      return;
    }

    setBusy(true);
    try {
      const engineContentType = UI_TO_ENGINE_CONTENT_TYPE[generateForm.content_type] || 'comparison_script';
      const payload = {
        tool_id: generateForm.tool_id,
        content_type: engineContentType,
        platforms: generateForm.platforms,
        ai_guidance: generateForm.ai_guidance || '',
        media_assets: generateForm.media_assets || [],
      };

      const response = await adminApi.generateContent(payload);
      const toolName = tools.find((t) => t.id === generateForm.tool_id)?.name || 'Tool';

      const generated = normalizeQueueItem({
        id: response?.data?.id,
        tool_id: generateForm.tool_id,
        tool_name: toolName,
        title: response?.data?.title || `${toolName} ${generateForm.content_type}`,
        content_type: response?.data?.content_type || engineContentType,
        platforms: response?.data?.platforms || generateForm.platforms,
        status: response?.data?.status || 'generated',
        scheduled_at: response?.data?.created_at || nowIso(),
        created_at: response?.data?.created_at || nowIso(),
        content: response?.data?.content || {},
      });

      setQueueItems((prev) => [generated, ...prev]);
      setShowGenerateModal(false);
      setActiveTab('campaigns');
      setGenerateForm({
        tool_id: generateForm.tool_id,
        content_type: 'reel',
        platforms: ['Instagram'],
        ai_guidance: '',
        media_assets: [],
      });
    } catch (error) {
      console.error('Failed to generate content:', error);
      alert('Content generation failed.');
    } finally {
      setBusy(false);
    }
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const payload = {
        auto_schedule_enabled: !!settings.auto_schedule_enabled,
        newsletter_auto_send: !!settings.newsletter_auto_send,
        sitemap_auto_ping: !!settings.sitemap_auto_ping,
        require_approval_before_publish: !!settings.require_approval_before_publish,
        smart_posting_windows_enabled: !!settings.smart_posting_windows_enabled,
        auto_repurpose_enabled: !!settings.auto_repurpose_enabled,
        auto_utm_enabled: !!settings.auto_utm_enabled,
        ab_variant_count: Number(settings.ab_variant_count || 1),
        recycle_after_days: Number(settings.recycle_after_days || 14),
        campaign_tag: settings.campaign_tag || '',
        utm_source: settings.utm_source || '',
        utm_medium: settings.utm_medium || '',
        utm_campaign: settings.utm_campaign || '',
      };

      try {
        await adminApi.saveSettingsPost(payload);
      } catch {
        await adminApi.updateSettings(payload);
      }
      alert('Settings saved.');
    } catch (error) {
      console.error('Failed to save settings:', error);
      alert('Failed to save settings.');
    } finally {
      setSavingSettings(false);
    }
  };

  const saveIntegrationSettings = async () => {
    setSavingOpsConfig(true);
    try {
      await adminApi.updateOpsConfig(opsConfig);
      const diagnosticsResponse = await adminApi.validateOpsConfig().catch(() => ({ data: { checks: [] } }));
      setOpsDiagnostics(diagnosticsResponse.data?.checks || []);
      alert('Integration settings saved.');
    } catch (error) {
      console.error('Failed to save integration settings:', error);
      alert('Failed to save integration settings.');
    } finally {
      setSavingOpsConfig(false);
    }
  };

  const toggleSettingsSection = (sectionId) => {
    setExpandedSettingsSections((prev) => ({ ...prev, [sectionId]: !prev[sectionId] }));
  };

  const handleLogout = () => {
    logout();
    navigate('/admin');
  };

  if (authLoading || loadingPage) {
    return (
      <div className="min-h-screen bg-void flex items-center justify-center">
        <div className="animate-spin rounded-none h-12 w-12 border-b-2 border-cyber-yellow" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-void" data-testid="marketing-page">
      <header className="bg-surface border-b border-white/10 sticky top-0 z-40">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Megaphone size={22} className="text-cyber-yellow" />
            <h1 className="text-2xl font-black text-cyber-yellow" style={{ fontFamily: 'Unbounded, sans-serif' }}>
              MARKETING AUTOMATION
            </h1>
            <Link to="/admin/dashboard" className="text-sm text-text-secondary hover:text-cyber-yellow transition-colors">
              Back to Admin
            </Link>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-text-secondary text-sm">{user?.full_name}</span>
            <button onClick={handleLogout} className="btn-secondary rounded-none inline-flex items-center gap-2 text-sm">
              <LogOut size={15} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 py-8">
        <div className="terminal-grid p-2 rounded-none mb-6">
          <div className="flex gap-2 overflow-x-auto">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className="rounded-none px-4 py-2 text-sm font-semibold inline-flex items-center gap-2 transition-colors"
                style={{
                  background: activeTab === tab.id ? 'var(--accent-primary)' : 'transparent',
                  color: activeTab === tab.id ? '#000' : 'var(--text-secondary)',
                  border: `1px solid ${activeTab === tab.id ? 'var(--accent-primary)' : 'var(--border-subtle)'}`,
                }}
                data-testid={`marketing-tab-${tab.id}`}
              >
                <tab.icon size={15} />
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <WorkflowRail stages={workflowStages} activeTab={activeTab} nextStage={nextStage} onSelect={setActiveTab} />

        {activeTab === 'queue' && (
          <motion.section initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
            <StageBanner
              title="Create campaign assets"
              description="Generate one or more pieces first. These queue items become the raw material for campaign scheduling, repurposing, and automated dispatch."
              messages={[
                ...(!integrationState.makeReady ? ['Setup is incomplete: add a Make webhook if this cycle should dispatch automatically.'] : []),
                ...(queueItems.length === 0 ? ['Nothing is queued yet. Start by generating a piece of content for at least one platform.'] : []),
              ]}
              nextActionLabel={queueItems.length === 0 ? 'Generate content now' : 'Go to campaign launch'}
              onNext={() => (queueItems.length === 0 ? setShowGenerateModal(true) : setActiveTab('campaigns'))}
            />

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <h2 className="text-2xl text-white font-bold" style={{ fontFamily: 'Unbounded, sans-serif' }}>Content Queue</h2>
                <InfoTooltip text="All content waiting to be published. Review before publishing, or let automation handle scheduling based on platform posting windows." />
              </div>
              <div className="flex gap-2">
                <button onClick={refreshQueue} disabled={busy} className="btn-secondary rounded-none inline-flex items-center gap-2 text-sm">
                  <RefreshCw size={14} /> Refresh
                </button>
                <button onClick={publishTodayBatch} disabled={busy || !integrationState.makeReady} className="btn-secondary rounded-none inline-flex items-center gap-2 text-sm">
                  <Send size={14} /> Run today&apos;s publish
                </button>
                <button onClick={() => setShowGenerateModal(true)} className="btn-primary rounded-none inline-flex items-center gap-2 text-sm" data-testid="open-generate-modal">
                  <Plus size={15} /> Generate content
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
              <div className="relative">
                <MetricCard title="Queued now" value={String(queueFeatureStats.queued)} />
                <InfoTooltip text="Content items waiting for approval or scheduling" />
              </div>
              <div className="relative">
                <MetricCard title="Pending approval" value={String(queueFeatureStats.pendingApproval)} />
                <InfoTooltip text="Items awaiting editor review before publishing" />
              </div>
              <div className="relative">
                <MetricCard title="Repurpose candidates" value={String(queueFeatureStats.repurposeCandidates)} />
                <InfoTooltip text="Published items with low engagement eligible for recycling into new formats" />
              </div>
              <div className="relative">
                <MetricCard title="UTM coverage" value={`${queueFeatureStats.utmCoverage}%`} />
                <InfoTooltip text="Percentage of queued items with UTM tracking parameters applied" />
              </div>
            </div>

            <div className="terminal-grid rounded-none p-4">
              <h3 className="text-lg text-white font-semibold mb-3" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                Pro Automation Controls
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                <div className="p-3 border border-white/10 bg-white/[0.02]">
                  <div className="text-cyber-yellow font-semibold">Approval Workflow</div>
                  <p className="text-text-secondary mt-1">Gate publish actions until approved by editor (inspired by Sprout/Hootsuite team workflows).</p>
                </div>
                <div className="p-3 border border-white/10 bg-white/[0.02]">
                  <div className="text-cyber-yellow font-semibold">Smart Posting Windows</div>
                  <p className="text-text-secondary mt-1">Apply platform-specific posting windows based on benchmark behavior from top social suites.</p>
                </div>
                <div className="p-3 border border-white/10 bg-white/[0.02]">
                  <div className="text-cyber-yellow font-semibold">Auto Repurposing</div>
                  <p className="text-text-secondary mt-1">Recycle underperforming pieces into alternate formats after a cooldown period.</p>
                </div>
                <div className="p-3 border border-white/10 bg-white/[0.02]">
                  <div className="text-cyber-yellow font-semibold">UTM Governance</div>
                  <p className="text-text-secondary mt-1">Enforce default UTM templates for channel attribution consistency.</p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3">
              {queueItems.length === 0 && (
                <div className="terminal-grid rounded-none p-8 text-center text-text-secondary">No queued content found.</div>
              )}

              {queueItems.map((item) => (
                <div key={item.id} className="terminal-grid rounded-none p-4">
                  <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
                    <div className="flex items-center gap-3 flex-wrap">
                      <h3 className="text-white font-semibold">{item.tool_name}</h3>
                      <span className={`px-2 py-1 text-xs border rounded-none ${TYPE_CLASS[item.content_type] || TYPE_CLASS.short}`}>
                        {item.content_type}
                      </span>
                      <span className={`px-2 py-1 text-xs border rounded-none ${STATUS_CLASS[item.status] || STATUS_CLASS.queued}`}>
                        {item.status}
                      </span>
                    </div>
                    <div className="text-xs text-text-muted font-mono inline-flex items-center gap-1">
                      <CalendarDays size={12} /> {new Date(item.scheduled_at).toLocaleString()}
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2 mb-3">
                    {(item.platforms || []).map((platform) => (
                      <span key={platform} className="px-2 py-1 text-xs border border-white/20 text-text-secondary rounded-none">
                        {platform}
                      </span>
                    ))}
                  </div>

                  <div>
                    <button
                      onClick={() => publishQueueItem(item.id)}
                      disabled={busy || item.status === 'published'}
                      className="btn-primary rounded-none inline-flex items-center gap-2 text-xs mr-2"
                    >
                      <Send size={13} /> Publish / schedule
                    </button>
                    <button
                      onClick={() => markPublished(item.id)}
                      disabled={item.status === 'published'}
                      className="btn-secondary rounded-none inline-flex items-center gap-2 text-xs"
                    >
                      <Check size={13} /> Mark published
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </motion.section>
        )}

        {activeTab === 'campaigns' && (
          <motion.section initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="space-y-6" data-testid="campaigns-tab">
            <StageBanner
              title="Launch the campaign"
              description="A novice should be able to complete the cycle here: save the campaign, decide whether it goes to cron or out immediately, and confirm that automation is ready."
              messages={campaignRequirements}
              nextActionLabel={campaignRequirements.length ? 'Fix required fields' : 'Open results'}
              onNext={() => setActiveTab(campaignRequirements.length ? 'settings' : 'published')}
            />

            <div className="flex items-center justify-between gap-4 flex-wrap">
              <h2 className="text-2xl text-white font-bold" style={{ fontFamily: 'Unbounded, sans-serif' }}>Campaign Builder</h2>
              <div className="flex gap-2 flex-wrap">
                <button
                  className="btn-secondary"
                  disabled={savingCampaign}
                  onClick={async () => {
                    setSavingCampaign(true);
                    try {
                      await saveCurrentCampaign();
                      alert('Campaign saved as a reusable draft.');
                    } catch {
                      alert('Failed to save campaign');
                    } finally {
                      setSavingCampaign(false);
                    }
                  }}
                >
                  {savingCampaign ? 'Saving...' : 'Save draft'}
                </button>
                <button
                  className="btn-secondary"
                  disabled={campaignActionBusy === 'schedule'}
                  onClick={() => automateCampaign('schedule')}
                >
                  {campaignActionBusy === 'schedule' ? 'Queueing...' : 'Queue for cron'}
                </button>
                <button
                  className="btn-primary"
                  disabled={campaignActionBusy === 'publish_now' || !integrationState.makeReady}
                  onClick={() => automateCampaign('publish_now')}
                >
                  {campaignActionBusy === 'publish_now' ? 'Publishing...' : 'Publish via Make now'}
                </button>
              </div>
            </div>
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Use this tab to finish the cycle: save the campaign, queue it for cron using the schedule field, or send it to Make immediately with one click.</p>

            <div className="terminal-grid rounded-none p-5">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                <MetricCard title="Saved campaigns" value={String(recentCampaigns.length)} />
                <MetricCard title="Ready for cron" value={String(recentCampaigns.filter((item) => String(item.status || '').toLowerCase() === 'scheduled').length)} />
                <MetricCard title="Sent / processing" value={String(recentCampaigns.filter((item) => ['sent', 'processing'].includes(String(item.status || '').toLowerCase())).length)} />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[['Campaign Title', 'title'], ['Subject Line', 'subject'], ['Preview Text', 'preview_text'], ['Audience Tag', 'audience_tag'], ['CTA Label', 'cta_label'], ['CTA URL', 'cta_url'], ['Schedule (ISO datetime)', 'scheduled_for']].map(([label, key]) => (
                  <div key={key} className="space-y-1">
                    <label className="text-xs uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>{label}</label>
                    <input
                      type={key === 'cta_url' ? 'url' : 'text'}
                      value={campaign[key]}
                      onChange={(e) => setCampaign((prev) => ({ ...prev, [key]: e.target.value }))}
                      className="w-full px-3 py-2 rounded text-sm"
                      style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)', color: 'var(--text-primary)' }}
                      placeholder={key === 'scheduled_for' ? '2026-04-24T09:00:00+00:00' : ''}
                    />
                  </div>
                ))}
                <div className="space-y-1">
                  <label className="text-xs uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Status</label>
                  <select
                    value={campaign.status}
                    onChange={(e) => setCampaign((prev) => ({ ...prev, status: e.target.value }))}
                    className="w-full px-3 py-2 rounded text-sm"
                    style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)', color: 'var(--text-primary)' }}
                  >
                    {['draft', 'queued', 'scheduled', 'sent'].map((s) => (<option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>))}
                  </select>
                </div>
              </div>
              <div className="mt-4 space-y-1">
                <label className="text-xs uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Campaign Content</label>
                <textarea
                  rows={8}
                  value={campaign.content}
                  onChange={(e) => setCampaign((prev) => ({ ...prev, content: e.target.value }))}
                  className="w-full px-3 py-2 rounded text-sm"
                  style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)', color: 'var(--text-primary)', resize: 'vertical' }}
                />
              </div>

              {campaignRequirements.length > 0 && (
                <div className="mt-4 border border-orange-400/30 bg-orange-500/8 p-4 rounded-none">
                  <div className="text-orange-300 font-semibold mb-2">Finish these items before launch</div>
                  <div className="space-y-2 text-sm text-text-secondary">
                    {campaignRequirements.map((item) => (
                      <div key={item}>- {item}</div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {recentCampaigns.length > 0 && (
              <div className="space-y-3">
                <h3 className="text-lg font-semibold text-white">Recent Campaigns</h3>
                <div className="space-y-2">
                  {recentCampaigns.map((item, i) => (
                    <div key={item.id || i} className="terminal-grid rounded-none p-4 flex items-start justify-between gap-4 flex-wrap">
                      <div>
                        <p className="text-white font-semibold">{item.title}</p>
                        <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{item.subject}</p>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <button
                          type="button"
                          onClick={() => setCampaign({ ...EMPTY_CAMPAIGN, ...item })}
                          className="btn-secondary rounded-none text-xs"
                        >
                          Load
                        </button>
                        <span className="px-2 py-1 text-xs uppercase font-mono" style={{ color: 'var(--accent-primary)', border: '1px solid var(--border-subtle)' }}>{item.status}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </motion.section>
        )}

        {activeTab === 'published' && (
          <motion.section initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="space-y-3">
            <div className="flex items-center gap-2">
              <h2 className="text-2xl text-white font-bold" style={{ fontFamily: 'Unbounded, sans-serif' }}>Published This Week</h2>
              <InfoTooltip text="Content that has been published in the last 7 days, sorted by recency. Includes engagement metrics from each platform." />
            </div>
            {publishedThisWeek.length === 0 && (
              <div className="terminal-grid rounded-none p-8 text-center text-text-secondary">No published items this week.</div>
            )}
            {publishedThisWeek.map((item) => (
              <div key={item.id} className="terminal-grid rounded-none p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <h3 className="text-white font-semibold">{item.title}</h3>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {(item.platforms || []).map((platform) => (
                      <span key={platform} className="px-2 py-1 text-xs border border-white/20 text-text-secondary rounded-none">
                        {platform}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="font-mono text-xs text-text-secondary grid grid-cols-3 gap-4 min-w-[220px]">
                  <div><span className="text-text-muted">views</span><div className="text-cyber-yellow">{item.views}</div></div>
                  <div><span className="text-text-muted">likes</span><div className="text-laser-green">{item.likes}</div></div>
                  <div><span className="text-text-muted">clicks</span><div className="text-cyber-yellow">{item.clicks}</div></div>
                </div>
              </div>
            ))}
          </motion.section>
        )}

        {activeTab === 'repurpose' && (
          <motion.section initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
            <div className="flex items-center gap-2">
              <h2 className="text-2xl text-white font-bold" style={{ fontFamily: 'Unbounded, sans-serif' }}>Repurpose Pipeline</h2>
              <InfoTooltip text="Run one-to-many repurposing, then manage outputs into downloadable or publishable media from one place." />
            </div>
            <RepurposeManager
              tools={tools}
              queueItems={queueItems}
              onRefreshQueue={refreshQueue}
              onMarkPublished={markPublished}
            />
          </motion.section>
        )}

        {activeTab === 'analytics' && (
          <motion.section initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
            <div className="flex items-center gap-2">
              <h2 className="text-2xl text-white font-bold" style={{ fontFamily: 'Unbounded, sans-serif' }}>Analytics</h2>
              <InfoTooltip text="Performance metrics for content created this month. Monitor which tools are most promoted and which platforms drive engagement." />
            </div>
            <MarketingAnalytics />
          </motion.section>
        )}

        {activeTab === 'settings' && (
          <motion.section initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
            <StageBanner
              title="Setup the automation stack"
              description="Finish this tab first. Without the right credentials, cron and one-click publishing cannot complete the cycle."
              messages={[
                ...(!integrationState.makeReady ? ['Make webhook is required for campaign automation and click-to-publish actions.'] : []),
                ...(!integrationState.bufferReady ? ['Buffer token is missing, so direct social publishing will fail.'] : []),
                ...(!integrationState.resendReady ? ['Resend is missing, so admin notifications will be skipped.'] : []),
              ]}
              nextActionLabel={(integrationState.makeReady && (integrationState.bufferReady || integrationState.resendReady)) ? 'Go to content creation' : null}
              onNext={() => setActiveTab('queue')}
            />

            <h2 className="text-2xl text-white font-bold" style={{ fontFamily: 'Unbounded, sans-serif' }}>Settings</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
              {(opsDiagnostics || []).map((item) => (
                <div key={item.name} className="terminal-grid rounded-none p-4">
                  <div className="flex items-center justify-between gap-3 mb-2">
                    <div className="text-sm text-white font-semibold">{item.name}</div>
                    <span className={`px-2 py-1 text-[10px] uppercase font-mono border ${item.status === 'active' || item.status === 'configured' ? 'text-laser-green border-laser-green/40 bg-laser-green/10' : 'text-orange-300 border-orange-300/40 bg-orange-500/10'}`}>
                      {item.status}
                    </span>
                  </div>
                  <div className="text-xs text-text-secondary">{item.detail}</div>
                </div>
              ))}
            </div>
            <div className="terminal-grid rounded-none p-5 space-y-4">
              <SettingsSection
                title="Automation Rules"
                sectionId="automation"
                isExpanded={!!expandedSettingsSections.automation}
                onToggle={toggleSettingsSection}
              >
                <div className="space-y-3">
                  <ToggleRow
                    label="Auto-schedule enabled"
                    checked={!!settings.auto_schedule_enabled}
                    onChange={(v) => setSettings((s) => ({ ...s, auto_schedule_enabled: v }))}
                    info="Automatically schedule queued content to post at optimal times determined by platform algorithms and your posting windows."
                  />
                  <ToggleRow
                    label="Newsletter auto-send"
                    checked={!!settings.newsletter_auto_send}
                    onChange={(v) => setSettings((s) => ({ ...s, newsletter_auto_send: v }))}
                    info="Automatically send curated newsletters to your subscriber list on a fixed schedule."
                  />
                  <ToggleRow
                    label="Sitemap auto-ping"
                    checked={!!settings.sitemap_auto_ping}
                    onChange={(v) => setSettings((s) => ({ ...s, sitemap_auto_ping: v }))}
                    info="Ping search engines with your updated sitemap whenever new content is published."
                  />
                  <ToggleRow
                    label="Require approval before publish"
                    checked={!!settings.require_approval_before_publish}
                    onChange={(v) => setSettings((s) => ({ ...s, require_approval_before_publish: v }))}
                    info="Gate all content publication behind an editor approval step. Team members must review before content goes live."
                  />
                  <ToggleRow
                    label="Smart posting windows"
                    checked={!!settings.smart_posting_windows_enabled}
                    onChange={(v) => setSettings((s) => ({ ...s, smart_posting_windows_enabled: v }))}
                    info="Automatically target platform-specific optimal posting times (e.g., LinkedIn posts Tue-Thu morning, TikTok evenings)."
                  />
                  <ToggleRow
                    label="Auto repurpose underperforming content"
                    checked={!!settings.auto_repurpose_enabled}
                    onChange={(v) => setSettings((s) => ({ ...s, auto_repurpose_enabled: v }))}
                    info="Automatically convert underperforming content (low engagement) into alternate formats on other platforms after cooldown period."
                  />
                </div>
              </SettingsSection>

              <SettingsSection
                title="Publishing Controls"
                sectionId="posting"
                isExpanded={!!expandedSettingsSections.posting}
                onToggle={toggleSettingsSection}
              >
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <label className="text-sm text-text-secondary">A/B variants per post</label>
                      <InfoTooltip text="Number of alternative versions of each post to test simultaneously. More variants = more data but higher content volume." />
                    </div>
                    <input
                      type="number"
                      min="1"
                      max="5"
                      className="input-pro rounded-none"
                      value={settings.ab_variant_count || 2}
                      onChange={(e) => setSettings((s) => ({ ...s, ab_variant_count: e.target.value }))}
                    />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <label className="text-sm text-text-secondary">Recycle after (days)</label>
                      <InfoTooltip text="Wait X days before repurposing content into a different format. Prevents audience fatigue." />
                    </div>
                    <input
                      type="number"
                      min="7"
                      max="90"
                      className="input-pro rounded-none"
                      value={settings.recycle_after_days || 21}
                      onChange={(e) => setSettings((s) => ({ ...s, recycle_after_days: e.target.value }))}
                    />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <label className="text-sm text-text-secondary">Default campaign tag</label>
                      <InfoTooltip text="Default campaign identifier for grouping related posts in analytics (e.g., 'spring-launch', 'product-X')." />
                    </div>
                    <input
                      type="text"
                      className="input-pro rounded-none"
                      value={settings.campaign_tag || ''}
                      onChange={(e) => setSettings((s) => ({ ...s, campaign_tag: e.target.value }))}
                    />
                  </div>
                </div>
              </SettingsSection>

              <SettingsSection
                title="Tracking and UTM"
                sectionId="tracking"
                isExpanded={!!expandedSettingsSections.tracking}
                onToggle={toggleSettingsSection}
              >
                <div className="space-y-3">
                  <ToggleRow
                    label="Auto-apply UTM template"
                    checked={!!settings.auto_utm_enabled}
                    onChange={(v) => setSettings((s) => ({ ...s, auto_utm_enabled: v }))}
                    info="Automatically append UTM parameters to links based on your configured defaults for campaign tracking and analytics."
                  />
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <label className="text-sm text-text-secondary">UTM Source</label>
                        <InfoTooltip text="UTM parameter to identify the source platform (usually 'wowrolla' or your brand name)." />
                      </div>
                      <input
                        type="text"
                        className="input-pro rounded-none"
                        value={settings.utm_source || ''}
                        onChange={(e) => setSettings((s) => ({ ...s, utm_source: e.target.value }))}
                      />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <label className="text-sm text-text-secondary">UTM Medium</label>
                        <InfoTooltip text="UTM parameter for the channel type (e.g., 'social', 'email', 'affiliate')." />
                      </div>
                      <input
                        type="text"
                        className="input-pro rounded-none"
                        value={settings.utm_medium || ''}
                        onChange={(e) => setSettings((s) => ({ ...s, utm_medium: e.target.value }))}
                      />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <label className="text-sm text-text-secondary">UTM Campaign</label>
                        <InfoTooltip text="UTM parameter for campaign name (override with specific campaigns per post if needed)." />
                      </div>
                      <input
                        type="text"
                        className="input-pro rounded-none"
                        value={settings.utm_campaign || ''}
                        onChange={(e) => setSettings((s) => ({ ...s, utm_campaign: e.target.value }))}
                      />
                    </div>
                  </div>
                </div>
              </SettingsSection>

              <SettingsSection
                title="Recommended Posting Windows"
                sectionId="windows"
                isExpanded={!!expandedSettingsSections.windows}
                onToggle={toggleSettingsSection}
              >
                <div className="terminal-grid rounded-none p-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm font-mono">
                    {Object.entries(RECOMMENDED_WINDOWS).map(([platform, slot]) => (
                      <div key={platform} className="flex items-center justify-between border border-white/10 px-3 py-2">
                        <span className="text-cyber-yellow">{platform}</span>
                        <span className="text-text-secondary">{slot}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </SettingsSection>

              <SettingsSection
                title="Integrations"
                sectionId="integrations"
                isExpanded={!!expandedSettingsSections.integrations}
                onToggle={toggleSettingsSection}
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm text-text-secondary">ConvertKit API Key</label>
                    <input type="password" className="input-pro rounded-none mt-2" value={opsConfig.convertkit_api_key || ''} onChange={(e) => setOpsConfig((s) => ({ ...s, convertkit_api_key: e.target.value }))} />
                  </div>
                  <div>
                    <label className="text-sm text-text-secondary">ConvertKit API Secret</label>
                    <input type="password" className="input-pro rounded-none mt-2" value={opsConfig.convertkit_api_secret || ''} onChange={(e) => setOpsConfig((s) => ({ ...s, convertkit_api_secret: e.target.value }))} />
                  </div>
                  <div>
                    <label className="text-sm text-text-secondary">Buffer access token</label>
                    <input type="password" className="input-pro rounded-none mt-2" value={opsConfig.buffer_access_token || ''} onChange={(e) => setOpsConfig((s) => ({ ...s, buffer_access_token: e.target.value }))} />
                  </div>
                  <div>
                    <label className="text-sm text-text-secondary">Make webhook URL</label>
                    <input type="url" className="input-pro rounded-none mt-2" value={opsConfig.make_webhook_url || ''} onChange={(e) => setOpsConfig((s) => ({ ...s, make_webhook_url: e.target.value }))} />
                  </div>
                  <div>
                    <label className="text-sm text-text-secondary">Resend API Key</label>
                    <input type="password" className="input-pro rounded-none mt-2" value={opsConfig.resend_api_key || ''} onChange={(e) => setOpsConfig((s) => ({ ...s, resend_api_key: e.target.value }))} />
                  </div>
                  <div>
                    <label className="text-sm text-text-secondary">Resend from email</label>
                    <input type="email" className="input-pro rounded-none mt-2" value={opsConfig.resend_from_email || ''} onChange={(e) => setOpsConfig((s) => ({ ...s, resend_from_email: e.target.value }))} />
                  </div>
                  <div>
                    <label className="text-sm text-text-secondary">Google Client ID</label>
                    <input type="text" className="input-pro rounded-none mt-2" value={opsConfig.google_client_id || ''} onChange={(e) => setOpsConfig((s) => ({ ...s, google_client_id: e.target.value }))} />
                  </div>
                  <div>
                    <label className="text-sm text-text-secondary">Google Client Secret</label>
                    <input type="password" className="input-pro rounded-none mt-2" value={opsConfig.google_client_secret || ''} onChange={(e) => setOpsConfig((s) => ({ ...s, google_client_secret: e.target.value }))} />
                  </div>
                  <div>
                    <label className="text-sm text-text-secondary">Google Redirect URI</label>
                    <input type="url" className="input-pro rounded-none mt-2" value={opsConfig.google_redirect_uri || ''} onChange={(e) => setOpsConfig((s) => ({ ...s, google_redirect_uri: e.target.value }))} />
                  </div>
                  <div>
                    <label className="text-sm text-text-secondary">Default timezone</label>
                    <input type="text" className="input-pro rounded-none mt-2" value={opsConfig.default_timezone || ''} onChange={(e) => setOpsConfig((s) => ({ ...s, default_timezone: e.target.value }))} />
                  </div>
                  <div>
                    <label className="text-sm text-text-secondary">Consulting leads email</label>
                    <input type="email" className="input-pro rounded-none mt-2" value={opsConfig.consulting_leads_email || ''} onChange={(e) => setOpsConfig((s) => ({ ...s, consulting_leads_email: e.target.value }))} />
                  </div>
                  <div>
                    <label className="text-sm text-text-secondary">Default partner manager email</label>
                    <input type="email" className="input-pro rounded-none mt-2" value={opsConfig.default_partner_manager_email || ''} onChange={(e) => setOpsConfig((s) => ({ ...s, default_partner_manager_email: e.target.value }))} />
                  </div>
                  <div className="md:col-span-2">
                    <label className="text-sm text-text-secondary">Flonnect webhook URL</label>
                    <input type="url" className="input-pro rounded-none mt-2" value={opsConfig.flonnect_webhook_url || ''} onChange={(e) => setOpsConfig((s) => ({ ...s, flonnect_webhook_url: e.target.value }))} />
                  </div>
                </div>
                <button onClick={saveIntegrationSettings} disabled={savingOpsConfig} className="btn-secondary rounded-none inline-flex items-center gap-2 w-fit mt-4">
                  <Save size={14} /> {savingOpsConfig ? 'Saving Integrations...' : 'Save Integrations'}
                </button>
              </SettingsSection>

              <button onClick={saveSettings} disabled={savingSettings} className="btn-primary rounded-none inline-flex items-center gap-2 w-fit">
                <Save size={14} /> {savingSettings ? 'Saving...' : 'Save Core Settings'}
              </button>
            </div>
          </motion.section>
        )}
      </div>

      {showGenerateModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" data-testid="generate-content-modal">
          <div className="terminal-grid rounded-none p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-xl font-bold text-white" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                Generate Content
              </h3>
              <button onClick={() => setShowGenerateModal(false)} className="text-text-muted hover:text-white">
                <X size={20} />
              </button>
            </div>

            <form onSubmit={submitGenerate} className="space-y-4">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <label className="text-sm text-text-secondary">Tool</label>
                  <InfoTooltip text="Select the AI tool that will generate this content. Each tool specializes in different content domains." />
                </div>
                <select
                  className="input-pro rounded-none"
                  value={generateForm.tool_id}
                  onChange={(e) => setGenerateForm((f) => ({ ...f, tool_id: e.target.value }))}
                  required
                >
                  <option value="">-- Select a tool --</option>
                  {tools.map((tool) => (
                    <option key={tool.id} value={tool.id}>{tool.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <div className="flex items-center gap-2 mb-2">
                  <label className="text-sm text-text-secondary">Platforms</label>
                  <InfoTooltip text="Choose target platforms. Content types will auto-filter to show only options available on your selected platforms." />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {PLATFORMS.map((platform) => {
                    const checked = (generateForm.platforms || []).includes(platform);
                    return (
                      <label key={platform} className="flex items-center gap-2 text-sm text-text-secondary p-2 border border-white/10 rounded-none cursor-pointer hover:bg-white/[0.04]">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => togglePlatform(platform)}
                        />
                        {platform}
                      </label>
                    );
                  })}
                </div>
              </div>

              {(generateForm.platforms || []).length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <label className="text-sm text-text-secondary">Content type</label>
                    <InfoTooltip text={`Select a format optimized for ${(generateForm.platforms || []).join('/')}. Each format has specific media requirements.`} />
                  </div>
                  <select
                    className="input-pro rounded-none"
                    value={generateForm.content_type}
                    onChange={(e) => setGenerateForm((f) => ({ ...f, content_type: e.target.value }))}
                  >
                    {(() => {
                      const validTypes = new Set();
                      (generateForm.platforms || []).forEach((platform) => {
                        const matrix = PLATFORM_CONTENT_MATRIX[platform];
                        if (matrix) Object.keys(matrix).forEach((t) => validTypes.add(t));
                      });
                      return [...validTypes].map((typeKey) => {
                        // Find the description from any matching platform
                        let description = '';
                        for (const platform of generateForm.platforms || []) {
                          const matrix = PLATFORM_CONTENT_MATRIX[platform];
                          if (matrix && matrix[typeKey]) {
                            description = matrix[typeKey].label;
                            break;
                          }
                        }
                        return (
                          <option key={typeKey} value={typeKey}>
                            {description}
                          </option>
                        );
                      });
                    })()}
                  </select>
                  
                  {(() => {
                    const primaryPlatform = (generateForm.platforms || [])[0];
                    const contentTypeInfo = primaryPlatform && PLATFORM_CONTENT_MATRIX[primaryPlatform]?.[generateForm.content_type];
                    const requiredMedia = getRequiredMediaForContent(primaryPlatform, generateForm.content_type);
                    return contentTypeInfo ? (
                      <div className="mt-2 p-3 border border-white/10 bg-white/[0.02] rounded-none text-xs text-text-secondary">
                        <div className="font-semibold text-cyber-yellow mb-1">{contentTypeInfo.description}</div>
                        <div>Required assets: {requiredMedia.map(m => MEDIA_TYPES[m]?.label || m).join(', ')}</div>
                      </div>
                    ) : null;
                  })()}
                </div>
              )}

              <div>
                <div className="flex items-center gap-2 mb-2">
                  <label className="text-sm text-text-secondary">AI Guidance (Natural Language)</label>
                  <InfoTooltip text="Describe in plain English what type of content you want generated. Include tone, style, key messages, and any specific angles. The AI will use this to guide generation." />
                </div>
                <textarea
                  className="input-pro rounded-none w-full h-24 resize-none"
                  placeholder="e.g., Create a catchy carousel post for software engineers about the benefits of AI automation. Tone: inspiring but technical. Include 3 key points..."
                  value={generateForm.ai_guidance}
                  onChange={(e) => setGenerateForm((f) => ({ ...f, ai_guidance: e.target.value }))}
                />
              </div>

              {(() => {
                const primaryPlatform = (generateForm.platforms || [])[0];
                const requiredMedia = getRequiredMediaForContent(primaryPlatform, generateForm.content_type);
                return requiredMedia.length > 0 ? (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <label className="text-sm text-text-secondary">Media Assets</label>
                      <InfoTooltip text="Select which media you'll provide or want the AI to help source. Check all that apply." />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      {requiredMedia.map((mediaType) => {
                        const mediaInfo = MEDIA_TYPES[mediaType];
                        const checked = (generateForm.media_assets || []).includes(mediaType);
                        return (
                          <label
                            key={mediaType}
                            className="flex items-center gap-2 text-sm text-text-secondary p-2 border border-white/10 rounded-none cursor-pointer hover:bg-white/[0.04]"
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={(e) => {
                                setGenerateForm((f) => {
                                  const assets = f.media_assets || [];
                                  return {
                                    ...f,
                                    media_assets: e.target.checked
                                      ? [...assets, mediaType]
                                      : assets.filter((m) => m !== mediaType),
                                  };
                                });
                              }}
                            />
                            {mediaInfo?.label || mediaType}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ) : null;
              })()}

              <button type="submit" disabled={busy} className="btn-primary rounded-none inline-flex items-center gap-2 w-full justify-center py-2">
                <Sparkles size={14} /> {busy ? 'Generating...' : 'Generate content'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const WorkflowRail = ({ stages, activeTab, nextStage, onSelect }) => (
  <div className="terminal-grid rounded-none p-4 mb-6">
    <div className="flex items-start justify-between gap-4 flex-wrap mb-3">
      <div>
        <div className="text-xs text-text-muted font-mono uppercase mb-1">Guided workflow</div>
        <div className="text-white font-semibold">Follow these stages in order to finish the marketing cycle.</div>
      </div>
      <button type="button" className="btn-secondary rounded-none text-xs" onClick={() => onSelect(nextStage)}>
        Jump to next required stage
      </button>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
      {stages.map((stage, index) => (
        <button
          key={stage.id}
          type="button"
          onClick={() => onSelect(stage.id)}
          className={`text-left p-4 border rounded-none transition-colors ${activeTab === stage.id ? 'border-cyber-yellow bg-cyber-yellow/8' : stage.complete ? 'border-laser-green/40 bg-laser-green/5' : 'border-white/10 bg-white/[0.02]'}`}
        >
          <div className="flex items-center justify-between gap-2 mb-2">
            <span className="text-xs font-mono uppercase text-text-muted">Stage {index + 1}</span>
            <span className={`text-[10px] uppercase font-mono ${stage.complete ? 'text-laser-green' : 'text-orange-300'}`}>
              {stage.complete ? 'Complete' : 'Needs work'}
            </span>
          </div>
          <div className="text-white font-semibold mb-1">{stage.title}</div>
          <div className="text-xs text-text-secondary">{stage.hint}</div>
        </button>
      ))}
    </div>
  </div>
);

const StageBanner = ({ title, description, messages = [], nextActionLabel, onNext }) => (
  <div className="border border-cyber-yellow/25 bg-cyber-yellow/6 rounded-none p-4">
    <div className="flex items-start justify-between gap-4 flex-wrap">
      <div>
        <div className="text-white font-semibold mb-1">{title}</div>
        <div className="text-sm text-text-secondary">{description}</div>
      </div>
      {nextActionLabel && onNext && (
        <button type="button" className="btn-secondary rounded-none text-xs" onClick={onNext}>
          {nextActionLabel}
        </button>
      )}
    </div>
    {messages.length > 0 && (
      <div className="mt-3 space-y-2 text-sm text-text-secondary">
        {messages.map((message) => (
          <div key={message}>- {message}</div>
        ))}
      </div>
    )}
  </div>
);

const MetricCard = ({ title, value }) => (
  <div className="terminal-grid rounded-none p-4">
    <div className="text-xs text-text-muted font-mono uppercase mb-2">{title}</div>
    <div className="text-xl text-cyber-yellow font-black" style={{ fontFamily: 'Unbounded, sans-serif' }}>{value}</div>
  </div>
);

const InfoTooltip = ({ text }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  return (
    <div className="relative inline-block">
      <button
        type="button"
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        onClick={() => setShowTooltip(!showTooltip)}
        className="text-text-muted hover:text-cyber-yellow transition-colors p-1"
        aria-label="Help"
      >
        <HelpCircle size={14} />
      </button>
      {showTooltip && (
        <div className="absolute left-full top-1/2 ml-3 w-56 -translate-y-1/2 p-2 bg-surface border border-cyber-yellow/40 rounded-none text-xs text-text-secondary shadow-lg z-50 pointer-events-none">
          {text}
          <div className="absolute top-1/2 right-full -translate-y-1/2 w-0 h-0 border-t-4 border-b-4 border-r-4 border-t-transparent border-b-transparent border-r-surface" />
        </div>
      )}
    </div>
  );
};

const ToggleRow = ({ label, checked, onChange, info }) => (
  <div className="flex items-center justify-between p-3 border border-cyber-yellow/20 bg-white/[0.02] rounded-none">
    <div className="flex items-center gap-2">
      <span className="text-sm text-text-secondary">{label}</span>
      {info && <InfoTooltip text={info} />}
    </div>
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={`w-16 h-8 px-1 transition-colors rounded-none border-2 ${checked ? 'bg-cyber-yellow border-cyber-yellow shadow-[0_0_0_1px_#FFE800,0_0_16px_rgba(255,232,0,0.45)]' : 'bg-[#1A1A1A] border-white/30'}`}
      aria-pressed={checked}
    >
      <span
        className={`block h-5 w-5 transition-transform ${checked ? 'translate-x-8 bg-black' : 'translate-x-0 bg-cyber-yellow'}`}
      />
    </button>
  </div>
);

const SettingsSection = ({ title, sectionId, isExpanded, onToggle, children }) => (
  <div className="border border-white/10 rounded-none overflow-hidden">
    <button
      type="button"
      className="w-full flex items-center justify-between px-4 py-3 bg-white/[0.03] hover:bg-white/[0.06] transition-colors"
      onClick={() => onToggle(sectionId)}
      aria-expanded={isExpanded}
    >
      <span className="text-sm font-semibold text-white uppercase tracking-wider">{title}</span>
      <span className="inline-flex items-center justify-center w-6 h-6 border border-white/20 text-cyber-yellow">
        {isExpanded ? <Minus size={14} /> : <Plus size={14} />}
      </span>
    </button>
    {isExpanded && <div className="p-4 space-y-3">{children}</div>}
  </div>
);

export default MarketingPage;

